import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateQueryStringParameter } from '../../../../../../../common/global/helpers';
import useMount from '../../../../../../../common/hooks/useMount';
import Loader from '../../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../../common/widgets/LoadingSpinner';
import {
  CardFormFactor,
  CardType,
  GiftCardBeneficiaryType,
} from '../../../../../enums';
import {
  CardProductModel,
  ComputedCardProductPriceModel,
  TopUpDetailsModel,
} from '../../../../../models/Cards';
import { Dictionary } from '../../../../../models/Categories';
import { ErrorState } from '../../../../../models/Errors';
import { resources } from '../../../../../resources/resources';
import { cardService } from '../../../../../services/card';
import { TopUpGenericCard } from '../../Common/TopUpGenericCard';
import { TopUpGiftCard } from '../../Common/TopUpGiftCard';
import {
  GenericCardData,
  GiftCardData,
  TopUpContextType,
} from '../../Common/types';
import { isGenericCardType } from '../../Common/utils';
import { SelectedCard, Step3Data } from '../common';

interface TopUpCardProps {
  selectedCard?: SelectedCard;
  step3: Step3Data;
  setStep3GiftData: (data: GiftCardData) => void;
  setStep3GenericData: (data: GenericCardData) => void;
  saveTopUpData: (
    productId: number,
    options: Dictionary<any>,
    serializedOptions: string,
    price: ComputedCardProductPriceModel,
  ) => void;
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
  clearErrors: (optionId: number | string) => void;
  cardFormFactor?: CardFormFactor;
}

interface TopUpCardState {
  showErrorPanel?: boolean;
}

export const TopUpCard = (props: TopUpCardProps) => {
  const {
    selectedCard,
    step3,
    setStep3GiftData,
    setStep3GenericData,
    saveTopUpData,
    errors,
    setErrors,
    clearErrors,
    cardFormFactor,
  } = props;
  const { t: translate } = useTranslation();
  const [state, setState] = useState<TopUpCardState>({});

  const loadData = async (
    cardTypeId: CardType,
    abortController?: AbortController,
  ) => {
    const resp = await cardService.getTopUpCardDetailsByCardType(
      cardTypeId,
      TopUpContextType.Buy,
      { signal: abortController?.signal },
    );

    if (abortController?.signal.aborted) return;

    if (resp.hasErrors) {
      setState({ showErrorPanel: true });
      return;
    }

    const { cardProducts, thumbnail, topUpDetails } = resp.data;

    const updateProductIdParam = (details?: TopUpDetailsModel) => {
      if (details) {
        // only to be displayed for the ops team, not actually used in code
        updateQueryStringParameter('productId', details.productId.toString());
      }
    };

    const isGenericType = [
      CardType.Vacation,
      CardType.Meal,
      CardType.Culture,
      CardType.Blue,
    ].includes(cardTypeId);

    if (cardProducts.length === 1) {
      updateProductIdParam(topUpDetails);

      if (isGenericType) {
        setStep3GenericData({
          thumbnail,
          products: cardProducts,
          selectedProduct: cardProducts[0],
          topUpDetails,
        });
      } else if (cardTypeId === CardType.Gift) {
        setStep3GiftData({
          thumbnail,
          showBeneficiaryTypeSelector: false,
          beneficiaryType: cardProducts[0].beneficiaryType,
          products: cardProducts,
          selectedProduct: cardProducts[0],
          topUpDetails,
        });
      }

      return;
    }

    if (isGenericType) {
      updateProductIdParam(topUpDetails);

      setStep3GenericData({
        thumbnail,
        products: cardProducts,
        selectedProduct: cardProducts.find(
          e => e.productId === topUpDetails?.productId,
        ),
        topUpDetails,
      });
      return;
    }

    if (cardTypeId === CardType.Gift) {
      const employeeProducts = resp.data.cardProducts.filter(
        e => e.beneficiaryType === GiftCardBeneficiaryType.Employee,
      );
      const dependentProducts = resp.data.cardProducts.filter(
        e => e.beneficiaryType === GiftCardBeneficiaryType.Dependent,
      );
      const showBeneficiaryTypeSelector =
        !!employeeProducts.length && !!dependentProducts.length;

      let selectedProduct: CardProductModel | undefined;
      let topUpDetails: TopUpDetailsModel | undefined;

      if (!showBeneficiaryTypeSelector) {
        topUpDetails = resp.data.topUpDetails;
        selectedProduct = resp.data.cardProducts.find(
          e => e.productId === topUpDetails?.productId,
        );

        updateProductIdParam(topUpDetails);
      }

      let beneficiaryType = undefined;
      if (
        resp.data.cardProducts.every(
          e => e.beneficiaryType === GiftCardBeneficiaryType.Employee,
        )
      ) {
        beneficiaryType = GiftCardBeneficiaryType.Employee;
      }
      if (
        resp.data.cardProducts.every(
          e => e.beneficiaryType === GiftCardBeneficiaryType.Dependent,
        )
      ) {
        beneficiaryType = GiftCardBeneficiaryType.Dependent;
      }

      setStep3GiftData({
        thumbnail,
        products: cardProducts,
        showBeneficiaryTypeSelector,
        beneficiaryType,
        selectedProduct,
        topUpDetails,
      });
    }
  };

  useMount(abortController => {
    if (!selectedCard) return;
    if (
      (isGenericCardType(selectedCard.type) && !step3?.genericCard) ||
      (selectedCard.type === CardType.Gift && !step3?.giftCard)
    ) {
      (async () => {
        await loadData(selectedCard.type, abortController);
      })();
    }
  });

  if (!selectedCard) return null;

  return (
    <>
      <h1 className="card-step-title">
        {translate(resources.employeeCards.buy.step3.title)}
      </h1>
      {isGenericCardType(selectedCard.type) && (
        <Loader
          loading={!step3.genericCard}
          loader={<LoadingSpinner />}
          showErrorPanel={state.showErrorPanel}
        >
          {!!step3.genericCard && (
            <TopUpGenericCard
              contextType={TopUpContextType.Buy}
              cardType={selectedCard.type}
              cardFormFactor={cardFormFactor}
              genericCardData={step3.genericCard}
              setGenericCardData={newData => setStep3GenericData(newData)}
              onSaveClick={saveTopUpData}
              errors={errors}
              setErrors={setErrors}
              clearErrors={clearErrors}
              form={selectedCard.options}
            />
          )}
        </Loader>
      )}
      {selectedCard.type === CardType.Gift && (
        <Loader
          loading={!step3.giftCard}
          loader={<LoadingSpinner />}
          showErrorPanel={state.showErrorPanel}
        >
          {!!step3.giftCard && (
            <TopUpGiftCard
              contextType={TopUpContextType.Buy}
              cardFormFactor={cardFormFactor}
              giftCardData={step3.giftCard}
              setGiftCardData={newData => setStep3GiftData(newData)}
              onSaveClick={saveTopUpData}
              errors={errors}
              setErrors={setErrors}
              clearErrors={clearErrors}
              form={selectedCard.options}
            />
          )}
        </Loader>
      )}
    </>
  );
};
